import * as React from 'react';

import { Box, Flex } from '@lightspeed/flame/Core';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Text } from '@lightspeed/flame/Text';
import zxcvbn, { ZXCVBNResult } from 'zxcvbn';

import { createReactRenderer } from 'helpers/react-renderer';

interface Props {
  password1InputId: string;
  password2InputId: string;
  submitId?: string;
  minScore: number;
}

const MIN_LENGTH = 10;
const DEFAULT_MIN_SCORE = 2;

const PasswordValidator: React.FC<Props> = ({
  password1InputId,
  password2InputId,
  submitId,
  minScore = DEFAULT_MIN_SCORE,
}) => {
  const [zxcvbnResult, setZxcvbnResult] = React.useState<ZXCVBNResult>();
  const [passwordLength, setPasswordLength] = React.useState(0);
  const [isPasswordValidLength, setIsPasswordValidLength] = React.useState(false);
  const [isPasswordValidScore, setIsPasswordValidScore] = React.useState(false);
  const [isPasswordMatch, setIsPasswordMatch] = React.useState(false);
  const [hasPasswordSuggestions, setHasPasswordSuggestions] = React.useState(false);

  const handleInputChange = React.useCallback(() => {
    const input1Value = String(
      (document.getElementById(password1InputId) as HTMLInputElement)?.value
    );
    const input2Value = String(
      (document.getElementById(password2InputId) as HTMLInputElement)?.value
    );

    const zxcvbnResult = zxcvbn(input1Value);
    setZxcvbnResult(zxcvbnResult);

    const passwordLength = input1Value.length;
    const isPasswordValidLength = passwordLength >= MIN_LENGTH;
    const isPasswordValidScore = zxcvbnResult.score >= minScore;
    const hasPasswordSuggestions = (zxcvbnResult?.feedback?.suggestions.length || 0) > 0;
    const isPasswordMatch =
      isPasswordValidLength && isPasswordValidScore ? input1Value === input2Value : false;

    setPasswordLength(passwordLength);
    setIsPasswordValidLength(isPasswordValidLength);
    setIsPasswordValidScore(isPasswordValidScore);
    setIsPasswordMatch(isPasswordMatch);
    setHasPasswordSuggestions(hasPasswordSuggestions);

    if (submitId) {
      if (isPasswordMatch && !hasPasswordSuggestions) {
        document.getElementById(submitId)?.removeAttribute('disabled');
      } else {
        document.getElementById(submitId)?.setAttribute('disabled', 'disabled');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    document.getElementById(password1InputId)?.addEventListener('input', handleInputChange);
    document.getElementById(password2InputId)?.addEventListener('input', handleInputChange);
    if (submitId) {
      document.getElementById(submitId)?.setAttribute('disabled', 'disabled');
    }

    return () => {
      document.getElementById(password1InputId)?.removeEventListener('input', handleInputChange);
      document.getElementById(password2InputId)?.removeEventListener('input', handleInputChange);

      if (submitId) {
        document.getElementById(submitId)?.removeAttribute('disabled');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box my={1}>
      <Flex mb={1} alignItems="center">
        {passwordLength >= 6 && passwordLength < MIN_LENGTH ? (
          <IconWarning data-test="password-length-icon" color="orange" />
        ) : (
          <IconVerified
            data-test="password-length-icon"
            color={isPasswordValidLength ? 'green' : 'gray-300'}
          />
        )}
        <Text ml={1} size="small">
          Password must be at least <var>{MIN_LENGTH}</var> characters long.
        </Text>
      </Flex>
      <Flex mb={1}>
        {isPasswordValidLength && hasPasswordSuggestions ? (
          <IconWarning data-test="password-score-icon" color="orange" />
        ) : (
          <IconVerified
            data-test="password-score-icon"
            color={isPasswordValidScore ? 'green' : 'gray-300'}
          />
        )}
        <Box ml={1}>
          <Text size="small">Password must be hard to guess.</Text>

          {isPasswordValidLength &&
            zxcvbnResult?.feedback?.suggestions.map((suggestion, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Text size="small" key={index}>
                {suggestion}
              </Text>
            ))}
        </Box>
      </Flex>
      <Flex>
        <IconVerified
          data-test="password-match-icon"
          color={isPasswordMatch ? 'green' : 'gray-300'}
        />
        <Text ml={1} size="small">
          Password must match.
        </Text>
      </Flex>
    </Box>
  );
};

const RenderedComponent = createReactRenderer({
  component: PasswordValidator,
  selector: '[data-react-root="PasswordValidator"]',
});

export default PasswordValidator;

history.onChangeState(
  [
    {
      name: 'admin.views.employee',
      form_name: 'view',
      tab: 'login',
      id: undefined,
    },
    {
      pathname: '/change_password.php',
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
